import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { useModal } from '../../../hooks/useModal';
import { supabase } from '../../../utils/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import InfoModal from '../../../components/ui/InfoModal';
import { getSafeSkillName } from '../../../utils/skillsUtils';
import ContactButton from '../../../components/candidates/ContactButton';
import Skeleton from '../../../components/ui/Skeleton';
import MatchScoreTooltip from './MatchScoreTooltip';
import ApplicationDetailsModal from '../../../components/modals/ApplicationDetailsModal';
import jobService from '../../../utils/jobService';
import CandidateRating from '../../../components/ratings/CandidateRating';
import '../../../styles/applications-grid.css';

const ApplicationsGrid = ({ 
  applications = [], 
  onAcceptApplication, 
  onRejectApplication, 
  onMessageCandidate: _onMessageCandidate,
  onScheduleInterview,
  onViewProfile: _onViewProfile,
  onRefresh,
  jobFilter = null, // New prop to filter by specific job ID
  viewMode = 'grid'
}) => {
  const { user } = useAuth();
  const { infoModal, showInfo, closeInfo } = useModal();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [skillsFilter, setSkillsFilter] = useState('');
  const [jobIdFilter, setJobIdFilter] = useState(jobFilter || '');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [directApplications, setDirectApplications] = useState([]);
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [jobs, setJobs] = useState([]); // Store jobs for filter dropdown
  const [isRecalculatingScores, setIsRecalculatingScores] = useState(false);
  const [matchThreshold, setMatchThreshold] = useState(75); // Auto-match threshold default 75%
  const [isAutoMatching, setIsAutoMatching] = useState(false); // Auto-match loading state
  // Application details modal state
  const [showApplicationDetails, setShowApplicationDetails] = useState(false);
  const [selectedApplicationDetails, setSelectedApplicationDetails] = useState(null);
  // Update job filter when prop changes
  useEffect(() => {
    setJobIdFilter(jobFilter || '');
  }, [jobFilter]);
  
  // Auto-load jobs for filter dropdown
  useEffect(() => {
    const fetchJobs = async () => {
      if (!user?.id) return;
      
      try {
        const { data, error } = await supabase
          .from('jobs')
          .select('id, title, status')
          .eq('employer_id', user.id)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching jobs for filter:', error);
          return;
        }

        setJobs(data || []);
      } catch (error) {
        console.error('Error fetching jobs:', error);
      }
    };

    fetchJobs();
  }, [user?.id]);

  // Auto-load applications if none were provided
  useEffect(() => {
    const fetchApplicationsData = async () => {
      if (!user?.id || applications.length > 0) return;
      
      try {
        setIsFetchingData(true);
        const { data, error } = await supabase
          .from('job_applications')
          .select(`
            id,
            job_id,
            applicant_id,
            cover_letter,
            proposed_rate,
            status,
            employer_notes,
            applied_at,
            updated_at,
            interview_scheduled_at,
            match_percentage,
            match_breakdown,
            match_factors,
            job:jobs!inner(
              id,
              title,
              employer_id,
              description,
              requirements,
              skills_required
            ),
            applicant:user_profiles!job_applications_applicant_id_fkey(
              id,
              full_name,
              email,
              bio,
              profile_picture,
              hourly_rate,
              skills,
              experience_years,
              location,
              title,
              rating,
              soft_skills
            )
          `)
          .eq('job.employer_id', user.id)
          .order('applied_at', { ascending: false });

        if (error) {
          console.error('Error fetching applications:', error);
          return;
        }

        // Transform the data to match the expected format
        const transformedApplications = (data || []).map(app => ({
          id: app.id,
          applicant_id: app.applicant_id,
          job_id: app.job_id,
          name: typeof app.applicant?.full_name === 'string' 
            ? app.applicant.full_name 
            : (typeof app.applicant?.full_name === 'object' 
              ? JSON.stringify(app.applicant.full_name) 
              : 'Unknown'),
          title: typeof app.applicant?.title === 'string' 
            ? app.applicant.title 
            : (typeof app.applicant?.title === 'object' 
              ? JSON.stringify(app.applicant.title) 
              : (app.applicant?.bio?.split('.')[0]) || 'Virtual Assistant'),
          avatar: app.applicant?.profile_picture || null,
          matchPercentage: app.match_percentage || 0,
          match_breakdown: app.match_breakdown || null,
          match_factors: app.match_factors || [],
          rating: app.applicant?.rating ?? 0,
          total_reviews: app.applicant?.total_reviews || 0,
          communication_rating: app.applicant?.communication_rating || 0,
          work_quality_rating: app.applicant?.work_quality_rating || 0,
          experience: app.applicant?.experience_years || 0,
          hourlyRate: app.applicant?.hourly_rate || 0,
          skills: Array.isArray(app.applicant?.skills) 
            ? app.applicant.skills.map(skill => {
                if (typeof skill === 'string') {
                  return skill;
                } else {
                  return getSafeSkillName(skill);
                }
              }).filter(skill => skill && skill.trim().length > 0)
            : (typeof app.applicant?.skills === 'object' && app.applicant?.skills !== null
              ? Object.keys(app.applicant.skills).filter(key => key && key.trim().length > 0)
              : []),
          location: typeof app.applicant?.location === 'string' 
            ? app.applicant.location 
            : (typeof app.applicant?.location === 'object' 
              ? JSON.stringify(app.applicant.location) 
              : 'Unknown'),
          appliedAt: app.applied_at ? new Date(app.applied_at).toLocaleDateString() : 'Recently',
          status: app.status || 'pending',
          jobTitle: app.job?.title || 'Unknown Job',
          employerNotes: app.employer_notes || '',
          interviewScheduledAt: app.interview_scheduled_at || null,
          proposedRate: app.proposed_rate || app.applicant?.hourly_rate || 0,
          coverLetter: app.cover_letter || ''
        }));
        
        setDirectApplications(transformedApplications);
      } catch (error) {
        console.error('Error fetching applications:', error);
      } finally {
        setIsFetchingData(false);
      }
    };

    fetchApplicationsData();
  }, [user?.id, applications.length]);

  // Use direct applications if no applications were provided via props
  const applicationsData = applications.length > 0 ? applications : directApplications;

  // Filter and sort applications
  const filteredApplications = useMemo(() => {
    let filtered = applicationsData.filter(app => {
      const matchesSearch = 
        (app?.name?.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (app?.title?.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (Array.isArray(app?.skills) && app?.skills.some(skill => {
          const skillStr = typeof skill === 'string' ? skill : String(skill);
          return skillStr.toLowerCase().includes(searchQuery.toLowerCase());
        }));
      
      const matchesStatus = statusFilter === 'all' || app?.status === statusFilter;
      
      const matchesSkills = !skillsFilter || 
        (Array.isArray(app?.skills) && app?.skills.some(skill => {
          const skillStr = typeof skill === 'string' ? skill : String(skill);
          return skillStr.toLowerCase().includes(skillsFilter.toLowerCase());
        }));
      
      // Add job filter logic
      const matchesJob = !jobIdFilter || app?.job_id === jobIdFilter;
      
      return matchesSearch && matchesStatus && matchesSkills && matchesJob;
    });

    // Sort applications
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b?.appliedAt || 0) - new Date(a?.appliedAt || 0);
        case 'oldest':
          return new Date(a?.appliedAt || 0) - new Date(b?.appliedAt || 0);
        case 'match':
          return (b?.matchPercentage || 0) - (a?.matchPercentage || 0);
        case 'rating':
          return (b?.rating || 0) - (a?.rating || 0);
        case 'experience':
          return (b?.experience || 0) - (a?.experience || 0);
        case 'rate':
          return (b?.hourlyRate || 0) - (a?.hourlyRate || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [applicationsData, searchQuery, statusFilter, skillsFilter, sortBy, jobIdFilter]);

  const handleSelectApplication = (appId) => {
    setSelectedApplications(prev => 
      prev.includes(appId) 
        ? prev.filter(id => id !== appId)
        : [...prev, appId]
    );
  };

  const handleSelectAll = () => {
    if (selectedApplications.length === filteredApplications.length) {
      setSelectedApplications([]);
    } else {
      setSelectedApplications(filteredApplications.map(app => app?.id).filter(Boolean));
    }
  };

  const handleBulkAction = async (action) => {
    if (!selectedApplications.length) return;
    
    setIsLoading(true);
    
    try {
      const promises = selectedApplications.map(async (appId) => {
        switch (action) {
          case 'accept':
            await updateApplicationStatus(appId, 'accepted');
            onAcceptApplication?.(appId);
            break;
          case 'reject':
            await updateApplicationStatus(appId, 'rejected');
            onRejectApplication?.(appId);
            break;
          case 'shortlist':
            await updateApplicationStatus(appId, 'shortlisted');
            break;
          default:
            break;
        }
      });
      
      await Promise.all(promises);
      
      // Refresh applications data
      if (onRefresh) {
        onRefresh();
      } else {
        // Manually refetch if onRefresh not provided
        const { data, error } = await supabase
          .from('job_applications')
          .select(`
            id,
            job_id,
            applicant_id,
            cover_letter,
            proposed_rate,
            status,
            employer_notes,
            applied_at,
            updated_at,
            interview_scheduled_at,
            match_percentage,
            match_breakdown,
            match_factors,
            job:jobs!inner(
              id,
              title,
              employer_id,
              description,
              requirements,
              skills_required
            ),
            applicant:user_profiles!job_applications_applicant_id_fkey(
              id,
              full_name,
              email,
              bio,
              profile_picture,
              hourly_rate,
              skills,
              experience_years,
              location,
              title,
              rating,
              soft_skills
            )
          `)
          .eq('job.employer_id', user.id)
          .order('applied_at', { ascending: false });
          
        if (!error && data) {
          const transformed = (data || []).map(app => ({
            id: app.id,
            applicant_id: app.applicant_id,
            job_id: app.job_id,
            name: typeof app.applicant?.full_name === 'string' 
              ? app.applicant.full_name 
              : (typeof app.applicant?.full_name === 'object' 
                ? JSON.stringify(app.applicant.full_name) 
                : 'Unknown'),
            title: typeof app.applicant?.title === 'string' 
              ? app.applicant.title 
              : (typeof app.applicant?.title === 'object' 
                ? JSON.stringify(app.applicant.title) 
                : (app.applicant?.bio?.split('.')[0]) || 'Virtual Assistant'),
            avatar: app.applicant?.profile_picture || null,
            matchPercentage: app.match_percentage || 0,
            match_breakdown: app.match_breakdown || null,
            match_factors: app.match_factors || [],
            rating: app.applicant?.rating ?? 0,
            total_reviews: app.applicant?.total_reviews || 0,
            communication_rating: app.applicant?.communication_rating || 0,
            work_quality_rating: app.applicant?.work_quality_rating || 0,
            experience: app.applicant?.experience_years || 0,
            hourlyRate: app.applicant?.hourly_rate || 0,
            skills: Array.isArray(app.applicant?.skills) 
              ? app.applicant.skills.map(skill => 
                  typeof skill === 'string' 
                    ? skill 
                    : (typeof skill === 'object' && skill !== null && skill.name 
                      ? skill.name 
                      : String(skill))
                )
              : (typeof app.applicant?.skills === 'object' && app.applicant?.skills !== null
                ? Object.keys(app.applicant.skills) 
                : []),
            location: typeof app.applicant?.location === 'string' 
              ? app.applicant.location 
              : (typeof app.applicant?.location === 'object' 
                ? JSON.stringify(app.applicant.location) 
                : 'Unknown'),
            appliedAt: app.applied_at ? new Date(app.applied_at).toLocaleDateString() : 'Recently',
            status: app.status || 'pending',
            jobTitle: app.job?.title || 'Unknown Job',
            employerNotes: app.employer_notes || '',
            interviewScheduledAt: app.interview_scheduled_at || null,
            proposedRate: app.proposed_rate || app.applicant?.hourly_rate || 0,
            coverLetter: app.cover_letter || ''
          }));
          
          setDirectApplications(transformed);
        }
      }
      
      setSelectedApplications([]);
    } catch (error) {
      console.error('Error performing bulk action:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Recalculate match scores for all applications
  const handleRecalculateScores = async () => {
    if (!jobIdFilter) {
      showInfo({
        title: 'Selection Required',
        message: 'Please select a specific job to recalculate match scores',
        type: 'warning'
      });
      return;
    }
    
    setIsRecalculatingScores(true);
    
    try {
      const result = await jobService.recalculateJobMatchScores(jobIdFilter);
      
      if (result.success) {
        showInfo({
          title: 'Success',
          message: `Successfully updated ${result.data.successful} application match scores`,
          type: 'success'
        });
        onRefresh?.(); // Refresh the applications data
      } else {
        showInfo({
          title: 'Error',
          message: `Failed to recalculate match scores: ${result.error}`,
          type: 'error'
        });
      }
    } catch (error) {
      showInfo({
        title: 'Error',
        message: 'Error recalculating match scores. Please try again.',
        type: 'error'
      });
      console.error('Recalculate scores error:', error);
    } finally {
      setIsRecalculatingScores(false);
    }
  };

  // Auto-match candidates based on match percentage threshold
  const handleAutoMatch = async () => {
    if (!jobIdFilter) {
      showInfo({
        title: 'Selection Required',
        message: 'Please select a specific job to auto-match candidates',
        type: 'warning'
      });
      return;
    }
    
    if (matchThreshold < 0 || matchThreshold > 100) {
      showInfo({
        title: 'Invalid Threshold',
        message: 'Please enter a match threshold between 0 and 100',
        type: 'error'
      });
      return;
    }
    
    setIsAutoMatching(true);
    
    try {
      const result = await jobService.autoMatchApplications(jobIdFilter, matchThreshold);
      
      if (result.success) {
        showInfo({
          title: 'Success',
          message: `Successfully auto-matched and shortlisted ${result.data.shortlisted} candidate(s) with ${matchThreshold}% or higher match score`,
          type: 'success'
        });
        
        // Always call onRefresh if provided to trigger parent component refresh
        if (onRefresh) {
          await onRefresh();
        }
      } else {
        showInfo({
          title: 'Error',
          message: `Failed to auto-match applications: ${result.error}`,
          type: 'error'
        });
      }
    } catch (error) {
      showInfo({
        title: 'Error',
        message: 'Error auto-matching applications. Please try again.',
        type: 'error'
      });
      console.error('Auto-match error:', error);
    } finally {
      setIsAutoMatching(false);
    }
  };

  // Update application status in Supabase
  const updateApplicationStatus = async (applicationId, status, notes = null) => {
    try {
      const updates = { 
        status, 
        updated_at: new Date().toISOString() 
      };
      
      if (notes) {
        updates.employer_notes = notes;
      }

      const { error } = await supabase
        .from('job_applications')
        .update(updates)
        .eq('id', applicationId);
      
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error updating application status:', error);
      return false;
    }
  };

  // Handle opening application details modal
  const handleViewApplicationDetails = (application) => {
    setSelectedApplicationDetails(application);
    setShowApplicationDetails(true);
  };

  const handleCloseApplicationDetails = () => {
    setShowApplicationDetails(false);
    setSelectedApplicationDetails(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-warning-100 text-warning';
      case 'shortlisted':
        return 'bg-primary-100 text-primary';
      case 'interviewed':
        return 'bg-accent-100 text-accent';
      case 'accepted':
        return 'bg-success-100 text-success';
      case 'rejected':
        return 'bg-error-100 text-error';
      default:
        return 'bg-secondary-100 text-text-secondary';
    }
  };

  if (isFetchingData) {
    return (
      <div className="space-y-6">
        {/* Loading skeleton for filters */}
        <div className="bg-surface rounded-lg border border-border p-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
            <Skeleton className="h-10 w-64" />
            <div className="flex flex-wrap items-center space-x-4">
              <Skeleton className="h-10 w-32" />
              <Skeleton className="h-10 w-40" />
            </div>
          </div>
        </div>
        
        {/* Loading skeleton for applications */}
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-surface rounded-lg border border-border p-6 animate-pulse">
            <div className="flex items-start space-x-4">
              <div className="w-4 h-4 rounded bg-gray-200"></div>
              <div className="w-16 h-16 rounded-full bg-gray-200"></div>
              <div className="flex-1">
                <div className="w-48 h-6 bg-gray-200 rounded mb-2"></div>
                <div className="w-32 h-4 bg-gray-200 rounded mb-4"></div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {[1, 2, 3].map(j => (
                    <div key={j} className="w-16 h-6 bg-gray-200 rounded"></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!applicationsData?.length) {
    return (
      <div className="text-center py-12">
        <Icon name="Users" size={48} className="text-text-muted mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-text-primary mb-2">No Applications Yet</h3>
        <p className="text-text-secondary">Applications will appear here once candidates start applying to your jobs.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters and Controls */}
      <div className="bg-surface rounded-lg border border-border p-4 shadow-sm">
        <div className="flex flex-col lg:flex-col lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
          {/* Search */}
          <div className="flex flex-1 max-w-md flex-row gap-4 justify-between mb-4">
            <div className="relative">
              <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" />
              <Input
                type="text"
                placeholder="Search by name, title, or skills..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-full"
              />
            </div>
          
          {/* Skills filter on larger screens */}
            <div className="hidden lg:block relative">
              <Icon name="Tag" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" />
              <Input
                type="text"
                placeholder="Filter by skill..."
                value={skillsFilter}
                onChange={(e) => setSkillsFilter(e.target.value)}
                className="pl-10 w-36"
              />
            </div>
          </div>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Job Filter */}
            <select
              value={jobIdFilter}
              onChange={(e) => setJobIdFilter(e.target.value)}
              className="px-3 py-2 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary bg-white"
            >
              <option value="">All Jobs</option>
              {jobs.map(job => (
                <option key={job.id} value={job.id}>
                  {(job.title || 'Untitled Job').substring(0, 50)}
                </option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary bg-white"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="shortlisted">Shortlisted</option>
              <option value="interviewed">Interviewed</option>
              <option value="accepted">Accepted</option>
              <option value="hired">Hired</option>
              <option value="rejected">Rejected</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary bg-white"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="match">Best Match</option>
              <option value="rating">Highest Rating</option>
              <option value="experience">Most Experience</option>
              <option value="rate">Highest Rate</option>
            </select>

            {/* Match Threshold Input - Show when job is selected */}
            {jobIdFilter && (
              <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <label htmlFor="match-threshold" className="text-sm text-amber-900 font-medium">
                  Min Match:
                </label>
                <input
                  id="match-threshold"
                  type="number"
                  min="0"
                  max="100"
                  value={matchThreshold}
                  onChange={(e) => setMatchThreshold(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
                  className="w-16 px-2 py-1 border border-amber-300 rounded text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
                <span className="text-sm text-amber-900">%</span>
              </div>
            )}

            {/* Clear filters button - show when filters are active */}
            {jobIdFilter && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRecalculateScores}
                disabled={isRecalculatingScores}
                iconName="RefreshCw"
                className="whitespace-nowrap"
              >
                {isRecalculatingScores ? 'Updating...' : 'Update Match Scores'}
              </Button>
            )}

            {/* Auto-Match Button */}
            {jobIdFilter && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleAutoMatch}
                disabled={isAutoMatching}
                iconName="Zap"
                className="whitespace-nowrap"
              >
                {isAutoMatching ? 'Auto-Matching...' : 'Auto-Match'}
              </Button>
            )}

            {(jobIdFilter || statusFilter !== 'all' || skillsFilter || searchQuery) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setJobIdFilter('');
                  setStatusFilter('all');
                  setSkillsFilter('');
                  setSearchQuery('');
                }}
                className="whitespace-nowrap"
              >
                Clear Filters
              </Button>
            )}
          </div>
        </div>

        {/* Job filter indicator */}
        {jobIdFilter && (
          <div className="mt-3 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <Icon name="Filter" size={16} className="text-blue-600" />
              <span className="text-sm text-blue-800">
                Filtering applications for: <strong>{jobs.find(j => j.id === jobIdFilter)?.title || 'Selected Job'}</strong>
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setJobIdFilter('')}
              className="text-blue-600 hover:text-blue-800"
            >
              <Icon name="X" size={16} />
            </Button>
          </div>
        )}

        {/* Skills filter on mobile */}
        <div className="lg:hidden mt-3">
          <div className="relative">
            <Icon name="Tag" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" />
            <Input
              type="text"
              placeholder="Filter by skill..."
              value={skillsFilter}
              onChange={(e) => setSkillsFilter(e.target.value)}
              className="pl-10 w-full"
            />
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedApplications.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <span className="text-sm text-text-secondary">
                {selectedApplications.length} application{selectedApplications.length !== 1 ? 's' : ''} selected
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('shortlist')}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center">
                      <span className="animate-spin h-4 w-4 mr-1 border-t-2 border-b-2 border-primary rounded-full"></span>
                      Shortlisting...
                    </span>
                  ) : 'Shortlist'}
                </Button>
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => handleBulkAction('accept')}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center">
                      <span className="animate-spin h-4 w-4 mr-1 border-t-2 border-b-2 border-white rounded-full"></span>
                      Accepting...
                    </span>
                  ) : 'Accept'}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleBulkAction('reject')}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center">
                      <span className="animate-spin h-4 w-4 mr-1 border-t-2 border-b-2 border-white rounded-full"></span>
                      Rejecting...
                    </span>
                  ) : 'Reject'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Applications Grid */}
      <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 gap-4' : 'space-y-4'}>
        {/* Select All Header */}
        <div className={`flex items-center justify-between bg-secondary-50 px-4 py-3 rounded-lg shadow-sm ${viewMode === 'grid' ? 'col-span-full' : ''}`}>
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={selectedApplications.length === filteredApplications.length && filteredApplications.length > 0}
              onChange={handleSelectAll}
              className="rounded border-secondary-300 text-primary focus:ring-primary"
              aria-label="Select all applications"
            />
            <span className="text-sm font-medium text-text-primary">
              {filteredApplications.length} application{filteredApplications.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="text-sm text-text-secondary hidden sm:block">
            Sorted by {sortBy.replace('_', ' ')}
          </div>
        </div>

        {filteredApplications.map((application) => (
          <div
            key={application?.id}
            className="bg-surface rounded-lg border border-border p-4 md:p-6 hover:border-primary hover:bg-blue-50/20 hover:shadow-md transition-all duration-200 block relative group"
          >
            <Link
              to={`/va-lookup/${application?.applicant_id}`}
              className="block absolute inset-0 rounded-lg"
              aria-hidden="true"
            />
            
            {/* Visual indicator that card is clickable */}
            <div className="absolute top-4 right-4 text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
              <Icon name="ExternalLink" size={20} className="text-primary" />
              <span className="sr-only">View public profile</span>
            </div>
            <div className="flex flex-col md:flex-row md:items-center gap-4 relative z-10">
              {/* Checkbox and Avatar - Mobile Layout */}
              <div className="flex items-center gap-3 md:hidden">
                <input
                  type="checkbox"
                  checked={selectedApplications.includes(application?.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleSelectApplication(application?.id);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="rounded border-secondary-300 text-primary focus:ring-primary"
                  aria-label={`Select ${application?.name}`}
                />
                <img
                  src={application?.avatar || '/assets/images/Logo/webp/VAS-LOGO.webp'}
                  alt={application?.name || 'Candidate'}
                  className="w-12 h-12 rounded-full object-contain"
                  onError={(e) => {
                    e.target.src = '/assets/images/Logo/webp/VAS-LOGO.webp';
                  }}
                />
                
                {/* Status Badge - Mobile */}
                <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(application?.status)}`}>
                  {application?.status || 'pending'}
                </span>
              </div>

              {/* Desktop Layout */}
              <div className="hidden md:block">
                <input
                  type="checkbox"
                  checked={selectedApplications.includes(application?.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleSelectApplication(application?.id);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="mt-1 rounded border-secondary-300 text-primary focus:ring-primary"
                  aria-label={`Select ${application?.name}`}
                />
              </div>

              {/* Avatar - Desktop */}
              <div className="hidden md:block flex-shrink-0">
                <img
                  src={application?.avatar || '/assets/images/Logo/webp/VAS-LOGO.webp'}
                  alt={application?.name || 'Candidate'}
                  className="w-16 h-16 rounded-full object-contain"
                  onError={(e) => {
                    e.target.src = '/assets/images/Logo/webp/VAS-LOGO.webp';
                  }}
                />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="group-hover:text-primary transition-colors duration-200">
                    <h3 className="text-lg font-semibold text-text-primary hover:text-primary cursor-pointer" title="Click to view public profile">
                      {typeof application?.name === 'string' 
                        ? application.name 
                        : (typeof application?.name === 'object' 
                          ? JSON.stringify(application.name) 
                          : 'Unknown Candidate')}
                    </h3>
                    <p className="text-text-secondary text-sm">
                      {typeof application?.title === 'string' 
                        ? application.title 
                        : (typeof application?.title === 'object' 
                          ? JSON.stringify(application.title) 
                          : 'Virtual Assistant')}
                    </p>
                  </div>

                  {/* Status Badge - Desktop */}
                  <span className={`hidden md:inline-block px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(application?.status)}`}>
                    {application?.status || 'pending'}
                  </span>
                </div>

                {/* Stats - Responsive layout */}
                <div className="flex flex-wrap items-center gap-3 sm:gap-6 mt-3">
                  <MatchScoreTooltip 
                    matchPercentage={application?.matchPercentage || 0}
                    matchFactors={application?.match_factors}
                    breakdown={application?.match_breakdown}
                  />

                  {/* {(application?.rating || application?.total_reviews) && (
                    <CandidateRating
                      candidateId={application?.applicant_id}
                      rating={application?.rating}
                      totalReviews={application?.total_reviews || 0}
                      showDetailed={true}
                      className="cursor-help"
                    />
                  )} */}

                  <div className="flex items-center space-x-1">
                    <Icon name="Briefcase" size={16} className="text-text-muted" />
                    <span className="text-sm text-text-secondary">
                      {application?.experience || 0} years
                    </span>
                  </div>

                  <div className="flex items-center space-x-1">
                    <Icon name="DollarSign" size={16} className="text-text-muted" />
                    <span className="text-sm text-text-secondary">
                      ${application?.proposedRate || application?.hourlyRate || 0}/hr
                    </span>
                  </div>

                  <div className="flex items-center space-x-1">
                    <Icon name="MapPin" size={16} className="text-text-muted" />
                    <span className="text-sm text-text-secondary">
                      {typeof application?.location === 'string' 
                        ? application.location 
                        : (typeof application?.location === 'object' 
                          ? JSON.stringify(application.location) 
                          : 'Unknown')}
                    </span>
                  </div>
                </div>

                {/* Skills */}
                {Array.isArray(application?.skills) && application.skills.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {application.skills.slice(0, 4).map((skill, index) => {
                      const skillText = getSafeSkillName(skill);
                      
                      return (
                        <span
                          key={index}
                          className="px-2 py-1 bg-secondary-100 text-text-secondary text-xs rounded-md"
                        >
                          {skillText}
                        </span>
                      );
                    })}
                    {application.skills.length > 4 && (
                      <span className="px-2 py-1 bg-secondary-100 text-text-secondary text-xs rounded-md">
                        +{application.skills.length - 4} more
                      </span>
                    )}
                  </div>
                )}

                {/* Applied Date and Job Title */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3 mt-3">
                  <p className="text-xs text-text-muted">
                    Applied {application?.appliedAt || 'recently'}
                  </p>
                  <div className="flex items-center mt-1 sm:mt-0">
                    <span className="text-xs text-text-muted sm:before:content-['•'] sm:before:mx-2 sm:before:text-text-muted">
                      <span className="inline-flex items-center">
                        <Icon name="Briefcase" size={12} className="mr-1 text-text-muted" />
                        {typeof application?.jobTitle === 'string' 
                          ? application.jobTitle 
                          : (typeof application?.jobTitle === 'object' 
                            ? JSON.stringify(application.jobTitle) 
                            : 'Unknown Job')}
                      </span>
                    </span>
                  </div>
                </div>
                
                {/* Mobile Actions */}
                <div className="flex flex-wrap gap-2 mt-4 md:hidden relative z-20">
                  
                  {(application?.status === 'accepted' || application?.status === 'shortlisted') && (
                    <ContactButton
                      vaId={application?.applicant_id}
                      vaName={application?.name}
                      jobId={application?.job_id}
                      variant="outline"
                      size="sm"
                      fullWidth
                      onClick={(e) => e.preventDefault()}
                    />
                  )}

                  <Button
                    variant="secondary"
                    size="sm"
                    iconName="FileText"
                    onClick={(e) => {
                      e.preventDefault();
                      handleViewApplicationDetails(application);
                    }}
                    fullWidth
                  >
                    View Details
                  </Button>

                  {application?.status === 'pending' && (
                    <div className="flex gap-2 w-full">
                      <Button
                        variant="success"
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          updateApplicationStatus(application?.id, 'accepted');
                          onAcceptApplication?.(application?.id);
                        }}
                        className="flex-1"
                      >
                        Accept
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          updateApplicationStatus(application?.id, 'rejected');
                          onRejectApplication?.(application?.id);
                        }}
                        className="flex-1"
                      >
                        Reject
                      </Button>
                    </div>
                  )}

                  {(application?.status === 'accepted' || application?.status === 'shortlisted') && (
                    <Button
                      variant="accent"
                      size="sm"
                      iconName="Calendar"
                      onClick={(e) => {
                        e.preventDefault();
                        onScheduleInterview?.(application?.id);
                      }}
                      fullWidth
                    >
                      Interview
                    </Button>
                  )}
                </div>
              </div>

              {/* Actions - Desktop */}
              <div className="hidden md:flex md:flex-shrink-0 md:flex-col md:space-y-2 relative z-20">
                
                {(application?.status === 'accepted' || application?.status === 'shortlisted') && (
                  <ContactButton 
                    vaId={application?.applicant_id}
                    vaName={application?.name}
                    jobId={application?.job_id}
                    variant="outline"
                    size="sm"
                  />
                )}

                <Button
                  variant="secondary"
                  size="sm"
                  iconName="FileText"
                  onClick={(e) => {
                    e.preventDefault();
                    handleViewApplicationDetails(application);
                  }}
                >
                  View Details
                </Button>

                {application?.status === 'pending' && (
                  <div className="flex space-x-1">
                    <Button
                      variant="success"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        updateApplicationStatus(application?.id, 'accepted');
                        onAcceptApplication?.(application?.id);
                      }}
                      className="flex-1"
                    >
                      Accept
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        updateApplicationStatus(application?.id, 'rejected');
                        onRejectApplication?.(application?.id);
                      }}
                      className="flex-1"
                    >
                      Reject
                    </Button>
                  </div>
                )}

                {(application?.status === 'accepted' || application?.status === 'shortlisted') && (
                  <Button
                    variant="accent"
                    size="sm"
                    iconName="Calendar"
                    onClick={(e) => {
                      e.preventDefault();
                      onScheduleInterview?.(application?.id);
                    }}
                  >
                    Interview
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* No Results */}
      {filteredApplications.length === 0 && (
        <div className="text-center py-12 bg-surface rounded-lg border border-border shadow-sm">
          <Icon name="Search" size={48} className="text-text-muted mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-text-primary mb-2">No Applications Found</h3>
          <p className="text-text-secondary">
            Try adjusting your search criteria or filters to find more applications.
          </p>
          <Button
            variant="outline"
            onClick={() => {
              setSearchQuery('');
              setStatusFilter('all');
              setSkillsFilter('');
            }}
            className="mt-4"
          >
            Clear Filters
          </Button>
        </div>
      )}

      {/* Application Details Modal */}
      <ApplicationDetailsModal
        isOpen={showApplicationDetails}
        onClose={handleCloseApplicationDetails}
        application={selectedApplicationDetails}
      />

      {/* Info Modal */}
      {infoModal && (
        <InfoModal
          title={infoModal.title}
          message={infoModal.message}
          type={infoModal.type}
          buttonLabel={infoModal.buttonLabel}
          onClose={closeInfo}
        />
      )}
    </div>
  );
};

export default ApplicationsGrid;

